import { webDomain } from './tools/web.ts';

export interface CitedSource {
  readonly url: string | null;
  readonly title: string;
  readonly site: string | null;
  readonly quote: string | null;
}

export interface CitedSpan {
  readonly start: number;
  readonly end: number;
  readonly sources: readonly CitedSource[];
}

const MAX_QUOTE = 300;

export function readCitedSpans(part: unknown): CitedSpan[] {
  if (!isRecord(part) || part.type !== 'text' || typeof part.text !== 'string') return [];
  if (!Array.isArray(part.citations)) return [];
  const length = part.text.length;
  const spans = new Map<string, { start: number; end: number; sources: CitedSource[] }>();
  for (const citation of part.citations) {
    if (!isRecord(citation)) continue;
    const source = readSource(citation.source, citation.citedText);
    if (source === null) continue;
    const start = offset(citation.startIndex, 0, length);
    const end = Math.max(start, offset(citation.endIndex, length, length));
    const key = `${start}:${end}`;
    const span = spans.get(key) ?? { start, end, sources: [] };
    spans.set(key, span);
    if (!span.sources.some((known) => sameSource(known, source))) span.sources.push(source);
  }
  return [...spans.values()].sort((a, b) => a.end - b.end || a.start - b.start);
}

export function citedSourcesOf(parts: readonly unknown[]): CitedSource[] {
  const sources: CitedSource[] = [];
  for (const part of parts) {
    for (const span of readCitedSpans(part)) {
      for (const source of span.sources) {
        if (!sources.some((known) => sameSource(known, source))) sources.push(source);
      }
    }
  }
  return sources;
}

function readSource(value: unknown, citedText: unknown): CitedSource | null {
  if (!isRecord(value)) return null;
  const url = typeof value.url === 'string' && isWebAddress(value.url) ? value.url : null;
  const site = url === null ? null : webDomain(url);
  const title = typeof value.title === 'string' ? oneLine(value.title) : '';
  if (url === null && title === '') return null;
  return { url, title: title || site || '', site, quote: readQuote(citedText) };
}

function readQuote(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const quote = oneLine(withoutInlineMarks(withoutTableRules(withoutLineMarks(value))));
  if (quote === '') return null;
  return quote.length > MAX_QUOTE ? `${quote.slice(0, MAX_QUOTE - 1).trimEnd()}…` : quote;
}

// A cited passage arrives as the page's markdown, and the card shows it as plain text.
function withoutLineMarks(value: string): string {
  return value.replace(/^[ \t]*(?:#{1,6}[ \t]+|>[ \t]?|[-*+][ \t]+)/gm, '');
}

function withoutTableRules(value: string): string {
  return value
    .replace(/\|(?:\s*:?-{3,}:?\s*\|)+/g, '|')
    .replace(/\|(?:\s*\|)+/g, '|')
    .replace(/^[\s|]+|[\s|]+$/g, '')
    .replace(/\s*\|\s*/g, ' · ');
}

function withoutInlineMarks(value: string): string {
  return value
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/(\*\*|__|~~)(\S(?:.*?\S)?)\1/g, '$2')
    .replace(/(^|[^\w*])\*(\S(?:[^*]*?\S)?)\*(?![\w*])/g, '$1$2')
    .replace(/(^|\W)_(\S(?:[^_]*?\S)?)_(?!\w)/g, '$1$2')
    .replace(/`([^`]*)`/g, '$1');
}

function offset(value: unknown, fallback: number, length: number): number {
  if (typeof value !== 'number' || !Number.isInteger(value)) return fallback;
  return Math.min(Math.max(value, 0), length);
}

function sameSource(a: CitedSource, b: CitedSource): boolean {
  return a.url !== null || b.url !== null ? a.url === b.url : a.title === b.title;
}

function isWebAddress(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}

function oneLine(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
